import { z } from "zod";

const access = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("ACCESS"),
    vlocityOfferId: z.string().optional(),
  }),
});

const cybersecurity = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("CYBERSECURITY"),
  }),
});

const modem = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("MODEM"),
    name: z.string().optional(),
  }),
});

const additionalTask = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("ADDITIONALTASK"),
  }),
  oneTimeItemPrice: z
    .object({
      grossAmount: z.number().optional(),
    })
    .optional(),
});

const internet = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("INTERNET"),
    login: z.string().optional(),
    password: z.string().optional(),
  }),
  recurringItemPrice: z.object({
    grossAmount: z.number(),
  }),
});

const commitment = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("COMMITMENT"),
  }),
});

const mobileAccess = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("MOBILE_ACCESS"),
    vlocityOfferId: z.string().optional(),
  }),
});

const simCard = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("SIM_CARD"),
    vlocityOfferId: z.string().optional(),
  }),
});

const voicePackage = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("VOICE_PACKAGE"),
    vlocityOfferId: z.string().optional(),
  }),
});

const wirelessStandard = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("5G"),
    vlocityOfferId: z.string().optional(),
  }),
});

const dataPackage = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("DATA_PACKAGE"),
    vlocityOfferId: z.string().optional(),
  }),
});

const mobileActivationPayment = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("MOBILE_ACTIVATION_PAYMENT"),
    vlocityOfferId: z.string().optional(),
  }),
});

const discountConvergentMobile = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("DISCOUNT_CONVERGENT_MOBILE"),
    vlocityOfferId: z.string().optional(),
  }),
});

const television = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("TELEVISION"),
  }),
});

const telcoOfferPromotion = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("TELCO_OFFER_PROMOTION"),
  }),
});

const discountMultiMobile = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("DISCOUNT_MULTI_MOBILE"),
  }),
});

const sfh = z.object({
  id: z.number(),
  product: z.object({
    productSpecificationId: z.literal("SFH"),
  }),
  recurringItemPrice: z.object({
    grossAmount: z.number(),
  }),
});

// TODO remove after https://dop-orange.atlassian.net/browse/DOP-54446 will be resolved
const empty = z.object({
  product: z.object({
    productSpecificationId: z.literal(""),
  }),
});

export const paymentStatus = z.enum([
  "NEW",
  "PENDING",
  "PENDING_REPEATING",
  "PENDING_REPEATING_RETRY",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
]);

export const cancellationReason = z.enum([
  "userRequest",
  "agentRequest",
  "unsuccessfulDelivery",
  "fulfillmentError",
  "orderSubmissionError",
  "NOSS_FAILED_BMT",
  "NOSS_FAILED_BMK",
  "fulfillmentExpired",
  "draftExpired",
]);

export const status = z.enum([
  "Completed",
  "PendingCancellation",
  "Cancelled",
  "FulfillmentFinishing",
  "InProgressFulfillment",
  "InProgressPayment",
  "InProgressDelivery",
  "Submitted",
  "Draft",
  "SubmitFailed",
]);

export const processId = z.enum([
  "NEW_NUMBER",
  "LEGACY_MIGRATION",
  "MNP_IN",
  "MSISDN_TRANSFER",
]);

export const mnpState = z.enum([
  "Processing",
  "ProposalSent",
  "Rejected",
  "Rejected4Manual",
  "Accepted",
  "CancelledInternally",
  "RequestSent",
  "Successful",
  "NetworkReconfigured",
  "SubscriptionCreated",
  "Waiting4Completion",
  "Completed",
  "CancellationInProgress",
  "Cancelled",
  "Failed",
  "Closed",
  "Cancelled14D",
  "RequestSent14D",
  "Successful14D",
  "NetworkReconfigured14D",
  "ProductOrderCancelling ",
  "Completed14D",
  "ProposalReceived",
  "RejectedSent",
  "ProposalAccepted",
  "AcceptedSent",
  "RequestReceived",
  "RequestApproved",
  "SubscriptionTerminated",
  "CompletedDeactivated",
]);

const mnpOrder = z.object({
  donor: z.string().optional(),
  sourceContractType: z.string().optional(),
  portingMode: z.string().optional(),
  transferDate: z.string().optional(),
  npCaseId: z.string().optional(),
  state: mnpState.optional(),
  npMessageCode: z.string().optional(),
  reasonDescription: z.string().optional(),
  customerMessage: z.string().optional(),
  donorName: z.string().optional(),
});

export const offerType = z.enum(["Mobile", "Ftth"]);

const appointment = z
  .object({
    startDate: z.string(),
  })
  .optional();

const subCategory = z.enum(["Mobile", "Ftth"]).optional();

const fulfillmentErrorDetails = z
  .object({
    mappedMessage: z.string().optional(),
    additionalMappedMessage: z.string().optional(),
  })
  .optional();

const installationAddress = z
  .object({
    street: z.string().optional(),
    streetNumber: z.string(),
    apartmentNumber: z.string(),
    municipality: z.string(),
    postCode: z.string(),
    isSfh: z.boolean().optional(),
  })
  .optional();

const delivery = z
  .object({
    street: z.string().optional(),
    streetNumber: z.string().optional(),
    apartmentNumber: z.string().optional(),
    municipality: z.string().optional(),
    postCode: z.string().optional(),
    deliveryType: z
      .enum(["COURIER", "BLISTER", "TECHNICAL_PARTNER_DELIVERY", "PREOWNED"])
      .optional(),
  })
  .optional();

const orderItem = z.union([
  access,
  cybersecurity,
  additionalTask,
  internet,
  modem,
  commitment,
  mobileAccess,
  dataPackage,
  voicePackage,
  wirelessStandard,
  simCard,
  mobileActivationPayment,
  television,
  discountMultiMobile,
  discountConvergentMobile,
  telcoOfferPromotion,
  sfh,
  empty,
]);

export const serverResponse = z.object({
  orderItem: z.array(orderItem),
  utechUserUrl: z.string().optional(),
  utechAgentUrl: z.string().optional(),
  paymentStatus: paymentStatus.optional(),
  status: status.optional(),
  id: z.string().optional(),
  adminUserId: z.string().optional(),
  processId: processId.optional(),
  dopUserId: z.string(),
  appointment,
  orderDate: z.string().optional(),
  installationAddress,
  delivery,
  subCategory,
  waybillNumber: z.string().optional(),
  cancellationReason: cancellationReason.optional(),
  fulfillmentErrorDetails,
  mnp: mnpOrder.optional(),
});

export type IOrderAPI = z.infer<typeof serverResponse>;
export type IInternet = z.infer<typeof internet>;
export type IAccess = z.infer<typeof access>;
export type ICyberSecurity = z.infer<typeof cybersecurity>;
export type IAdditionalTask = z.infer<typeof additionalTask>;
export type IModem = z.infer<typeof modem>;
export type IOrderPaymentStatus = z.infer<typeof paymentStatus>;
export type IOrderStatus = z.infer<typeof status>;
export type IOfferType = z.infer<typeof offerType>;
export type ISubCategory = z.infer<typeof subCategory>;
export type IOrderCancellationReason = z.infer<typeof cancellationReason>;
export type ISfh = z.infer<typeof sfh>;
export type IProcessIdStatus = z.infer<typeof processId>;
export type IMnpOrder = z.infer<typeof mnpOrder>;
